import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAdAccount } from "../lib/adAccount";
import { Pause, Play, MagnifyingGlass, Columns, Check, X, FloppyDisk } from "@phosphor-icons/react";
import DateRangePicker from "../components/DateRangePicker";
import RefreshButton from "../components/RefreshButton";
import LiveSyncButton from "../components/LiveSyncButton";

const fmtMoney = (n) => "RM" + (n || 0).toLocaleString("en-MY", { maximumFractionDigits: 2 });
const fmtBudget = (cents) => "RM" + (cents / 100).toLocaleString("en-MY");
const fmtNum = (n) => (n || 0).toLocaleString("en-MY");

// All available columns (FB Ads Manager style)
const ALL_COLUMNS = [
  { key: "status", label: "Status", group: "Delivery", align: "left", render: (c) => (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 ${
      c.status === "ACTIVE" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-800 text-zinc-500"
    }`}>
      <span className={`w-1 h-1 ${c.status === "ACTIVE" ? "bg-emerald-400" : "bg-zinc-500"}`} />
      {c.status}
    </span>
  )},
  { key: "name", label: "Name", group: "Delivery", align: "left", render: (c) => <span className="truncate max-w-xs inline-block">{c.name}</span> },
  { key: "objective", label: "Objective", group: "Delivery", align: "left", render: (c) => <span className="font-mono text-[11px] text-zinc-400">{c.objective?.replace("OUTCOME_", "")}</span> },
  { key: "daily_budget", label: "Daily Budget", group: "Delivery", align: "right", render: (c) => <span className="font-mono">{fmtBudget(c.daily_budget)}</span> },
  { key: "created_time", label: "Created", group: "Delivery", align: "left", render: (c) => <span className="font-mono text-[11px] text-zinc-500">{(c.created_time || "").slice(0, 10)}</span> },

  { key: "spend", label: "Spend", group: "Performance", align: "right", render: (c) => <span className="font-mono">{fmtMoney(c.spend)}</span> },
  { key: "impressions", label: "Impressions", group: "Performance", align: "right", render: (c) => <span className="font-mono">{fmtNum(c.impressions)}</span> },
  { key: "reach", label: "Reach", group: "Performance", align: "right", render: (c) => <span className="font-mono">{fmtNum(c.reach)}</span> },
  { key: "frequency", label: "Frequency", group: "Performance", align: "right", render: (c) => <span className="font-mono">{c.frequency || 0}</span> },

  { key: "clicks", label: "Clicks", group: "Engagement", align: "right", render: (c) => <span className="font-mono">{fmtNum(c.clicks)}</span> },
  { key: "ctr", label: "CTR", group: "Engagement", align: "right", render: (c) => <span className="font-mono">{c.ctr}%</span> },
  { key: "cpc", label: "CPC", group: "Engagement", align: "right", render: (c) => <span className="font-mono">{fmtMoney(c.cpc)}</span> },
  { key: "cpm", label: "CPM", group: "Engagement", align: "right", render: (c) => <span className="font-mono">{fmtMoney(c.cpm)}</span> },
  { key: "landing_page_views", label: "LP Views", group: "Engagement", align: "right", render: (c) => <span className="font-mono">{fmtNum(c.landing_page_views)}</span> },
  { key: "lp_view_rate", label: "LP View Rate", group: "Engagement", align: "right", render: (c) => <span className="font-mono">{c.lp_view_rate || 0}%</span> },

  { key: "subscribes", label: "Subscribes", group: "Subscribe", align: "right", render: (c) => <span className="font-mono font-semibold text-emerald-400">{fmtNum(c.subscribes)}</span> },
  { key: "subscribe_rate", label: "Subscribe Rate", group: "Subscribe", align: "right", render: (c) => <span className="font-mono">{c.subscribe_rate || 0}%</span> },
  { key: "cost_per_subscribe", label: "Cost / Subscribe", group: "Subscribe", align: "right", render: (c) => (
    <span className={`font-mono font-semibold ${c.cost_per_subscribe > 0 && c.cost_per_subscribe <= 12 ? "text-emerald-400" : c.cost_per_subscribe <= 20 ? "text-zinc-200" : "text-red-400"}`}>{fmtMoney(c.cost_per_subscribe)}</span>
  )},
];

const COLUMN_PRESETS = {
  Subscribe: ["status", "name", "objective", "daily_budget", "spend", "ctr", "landing_page_views", "subscribes", "cost_per_subscribe"],
  Performance: ["status", "name", "spend", "impressions", "ctr", "subscribes", "cost_per_subscribe"],
  Delivery: ["status", "name", "objective", "daily_budget", "created_time", "impressions", "reach", "frequency", "spend"],
  Engagement: ["status", "name", "spend", "impressions", "clicks", "ctr", "cpc", "landing_page_views", "lp_view_rate"],
};

function ColumnCustomizer({ visibleCols, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(visibleCols);
  const ref = useRef(null);

  useEffect(() => setDraft(visibleCols), [visibleCols, open]);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const groups = ALL_COLUMNS.reduce((acc, col) => {
    acc[col.group] = acc[col.group] || [];
    acc[col.group].push(col);
    return acc;
  }, {});

  const toggle = (key) => {
    setDraft((d) => d.includes(key) ? d.filter(k => k !== key) : [...d, key]);
  };

  const applyPreset = (name) => {
    setDraft(COLUMN_PRESETS[name]);
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="columns-button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-zinc-800 hover:border-zinc-700 px-2.5 py-1 text-xs font-mono text-zinc-300 transition-colors"
      >
        <Columns size={12} className="text-zinc-500" /> Columns ({visibleCols.length})
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-[#0A0A0A] border border-zinc-800 shadow-2xl z-50">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Customize Columns</div>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X size={12} /></button>
          </div>

          <div className="p-3 border-b border-zinc-800">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">// Preset</div>
            <div className="grid grid-cols-2 gap-1">
              {Object.keys(COLUMN_PRESETS).map((name) => (
                <button
                  key={name}
                  data-testid={`preset-cols-${name}`}
                  onClick={() => applyPreset(name)}
                  className="px-2 py-1 text-[11px] font-mono border border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {Object.entries(groups).map(([group, cols]) => (
              <div key={group} className="border-b border-zinc-900 last:border-b-0">
                <div className="px-3 py-1.5 text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-600 bg-zinc-950">{group}</div>
                {cols.map((col) => (
                  <label
                    key={col.key}
                    data-testid={`col-toggle-${col.key}`}
                    className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-zinc-900 text-xs"
                  >
                    <div className={`w-3.5 h-3.5 border flex items-center justify-center ${draft.includes(col.key) ? "bg-orange-500 border-orange-500" : "border-zinc-700"}`}>
                      {draft.includes(col.key) && <Check size={9} weight="bold" className="text-zinc-950" />}
                    </div>
                    <input type="checkbox" checked={draft.includes(col.key)} onChange={() => toggle(col.key)} className="hidden" />
                    <span className="text-zinc-300">{col.label}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>

          <div className="p-2 border-t border-zinc-800 flex justify-end gap-1">
            <button onClick={() => setOpen(false)} className="px-2 py-1 text-[11px] text-zinc-400 hover:text-zinc-100">Cancel</button>
            <button
              data-testid="cols-apply-button"
              onClick={apply}
              className="bg-orange-500 hover:bg-orange-600 text-zinc-950 text-[11px] font-semibold px-2.5 py-1 flex items-center gap-1"
            >
              <FloppyDisk size={11} weight="fill" /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Campaigns() {
  const { selected } = useAdAccount();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState({ mode: "preset", days: 7 });
  const [visibleCols, setVisibleCols] = useState(COLUMN_PRESETS.Subscribe);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load preferences once
  useEffect(() => {
    api.get("/me/preferences").then(r => {
      if (r.data?.campaign_columns) setVisibleCols(r.data.campaign_columns);
    });
  }, []);

  const load = async () => {
    if (!selected) return;
    setLoading(true);
    const params = new URLSearchParams({ ad_account_id: selected });
    if (range.mode === "preset") params.set("days", range.days);
    else { params.set("date_start", range.date_start); params.set("date_stop", range.date_stop); }
    const r = await api.get(`/campaigns?${params}`);
    setCampaigns(r.data || []);
    setLastUpdated(Date.now());
    setLoading(false);
  };

  const [pending, setPending] = useState(null); // { id, action, expiresAt }

  useEffect(() => { load(); }, [selected, range]); // eslint-disable-line

  // auto-clear pending confirm after 3s
  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => setPending(null), 3000);
    return () => clearTimeout(t);
  }, [pending]);

  const togglePause = async (c) => {
    const action = c.status === "ACTIVE" ? "pause" : "resume";
    if (!pending || pending.id !== c.id || pending.action !== action) {
      // First click → arm confirm state
      setPending({ id: c.id, action });
      return;
    }
    // Second click → execute
    setPending(null);
    try {
      if (action === "pause") await api.post(`/campaigns/${c.id}/pause`);
      else await api.post(`/campaigns/${c.id}/resume`);
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    }
    load();
  };

  const saveColumns = async (cols) => {
    setVisibleCols(cols);
    await api.post("/me/preferences", { campaign_columns: cols });
  };

  const filtered = campaigns.filter(c => {
    if (filter !== "all" && c.status !== filter) return false;
    if (query && !c.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const cols = ALL_COLUMNS.filter(c => visibleCols.includes(c.key));
  const rangeLabel = range.mode === "preset" ? `Last ${range.days} days` : `${range.date_start} → ${range.date_stop}`;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-10">
        <div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Campaigns</div>
          <div className="text-sm font-medium">All Campaigns · {rangeLabel}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <LiveSyncButton onSynced={load} />
          <RefreshButton lastUpdated={lastUpdated} loading={loading} onRefresh={load} />
          <DateRangePicker value={range} onChange={setRange} />
          <ColumnCustomizer visibleCols={visibleCols} onChange={saveColumns} />
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              data-testid="campaign-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search campaigns..."
              className="w-full bg-[#0A0A0A] border border-zinc-800 pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:border-orange-500"
            />
          </div>
          <div className="flex items-center gap-1 border border-zinc-800 p-0.5">
            {["all", "ACTIVE", "PAUSED"].map(f => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 text-[11px] font-mono tracking-wider uppercase ${filter === f ? "bg-orange-500 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="text-zinc-500 text-sm">Loading...</div>}

        <div className="bg-zinc-900/40 border border-zinc-800/60 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500 whitespace-nowrap">
                {cols.map((col) => (
                  <th key={col.key} data-testid={`th-${col.key}`} className={`${col.align === "right" ? "text-right" : "text-left"} px-3 py-2.5 font-medium`}>
                    {col.label}
                  </th>
                ))}
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-zinc-800/40 hover:bg-zinc-900/40" data-testid={`campaign-row-${c.id}`}>
                  {cols.map((col) => (
                    <td key={col.key} className={`${col.align === "right" ? "text-right" : "text-left"} px-3 py-3`}>
                      {col.render(c)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    {(() => {
                      const armed = pending && pending.id === c.id;
                      const isPause = c.status === "ACTIVE";
                      return (
                        <button
                          data-testid={`toggle-${c.id}`}
                          onClick={() => togglePause(c)}
                          className={`px-2 py-1.5 transition-all flex items-center gap-1 ml-auto ${
                            armed
                              ? `${isPause ? "bg-amber-500 text-zinc-950" : "bg-emerald-500 text-zinc-950"} animate-pulse`
                              : "hover:bg-zinc-800"
                          }`}
                          title={armed ? "Click again to confirm" : (isPause ? "Pause" : "Resume")}
                        >
                          {isPause ? <Pause size={13} weight={armed ? "fill" : "regular"} /> : <Play size={13} weight="fill" className={armed ? "" : "text-emerald-400"} />}
                          {armed && (
                            <span className="text-[10px] font-mono uppercase tracking-wider">Confirm?</span>
                          )}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={cols.length + 1} className="px-4 py-12 text-center text-zinc-500 text-sm">No campaigns found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
